import ms from 'ms';
import delay from 'delay';
import lodash from 'lodash';
import { synchronizeTime } from './helpers.js';
import generateRemoteConfigUrl from './generate-remote-config-url.js';
import { safeString } from '@exodus/safe-string';
const { get, isEqual } = lodash;
const getModificationIndicator = (response) => response.headers.get('Last-Modified') || response.headers.get('ETag');
export class RemoteConfig {
    #started = false;
    #isUpdating = false;
    #current;
    #remoteConfigAtom;
    #remoteConfigUrl;
    #getBuildMetadata;
    #errorTracking;
    #logger;
    #fetch;
    #freeze;
    #resolveLoaded;
    #whenLoaded = new Promise((resolve) => {
        this.#resolveLoaded = resolve;
    });
    #config;
    #timer;
    constructor({ errorTracking, logger, freeze, fetch, config, getBuildMetadata, remoteConfigAtom, }) {
        this.#errorTracking = errorTracking;
        this.#logger = logger;
        this.#fetch = (...args) => fetch(...args);
        this.#freeze = freeze;
        this.#config = config || Object.create(null);
        this.#remoteConfigUrl = config?.remoteConfigUrl;
        this.#getBuildMetadata = getBuildMetadata;
        this.#remoteConfigAtom = remoteConfigAtom;
    }
    get remoteConfigAtom() {
        return this.#remoteConfigAtom;
    }
    get = async (key) => {
        await this.#whenLoaded;
        return get(this.#current, key);
    };
    getAll = async () => {
        await this.#whenLoaded;
        return this.#current;
    };
    load = async () => {
        if (this.#started)
            return;
        const { fetchInterval = ms('2m'), errorBackoffTime = ms('5s') } = this.#config;
        this.#started = true;
        while (this.#started) {
            const { error } = await this.update();
            if (!this.#started)
                break;
            this.#timer = delay(error ? errorBackoffTime : fetchInterval);
            await this.#timer;
        }
    };
    sync = () => {
        this.#logger.warn('remoteConfig no longer supports the "sync" method');
    };
    stop = () => {
        this.#started = false;
        this.#timer?.clear();
    };
    update = async () => {
        if (this.#isUpdating)
            return { success: true };
        try {
            this.#isUpdating = true;
            const { config: current, lastModified: _lastModified } = await this.#remoteConfigAtom.get();
            if (!this.#current && current) {
                this.#current = this.#freeze(current);
            }
            const { config, lastModified } = await this.#fetchConfig({ lastModified: _lastModified });
            const changes = config && (!this.#current || !isEqual(config, this.#current));
            if (changes) {
                this.#current = this.#freeze(config);
                this.#logger.debug('updated config with changes from remote');
                void this.#remoteConfigAtom.set({
                    config,
                    lastModified,
                    remoteConfigUrl: this.#remoteConfigUrl,
                });
            }
            this.#resolveLoaded();
            return { success: true };
        }
        catch (error) {
            void this.#errorTracking.track({
                error: error,
                namespace: safeString `remoteConfig`,
            });
            this.#logger.error(error);
            return { error };
        }
        finally {
            this.#isUpdating = false;
        }
    };
    #fetchConfig = async ({ lastModified: currentLastModified, }) => {
        if (!this.#remoteConfigUrl) {
            this.#remoteConfigUrl = await generateRemoteConfigUrl(this.#getBuildMetadata);
        }
        const url = `${this.#remoteConfigUrl}?cacheBump=0`;
        const headers = { Accept: 'application/json' };
        const method = currentLastModified ? 'head' : 'get';
        this.#logger.debug(`trying to fetch remote config from ${this.#remoteConfigUrl} ...`);
        let response = await synchronizeTime(() => this.#fetch(url, { method, headers }));
        let lastModified = getModificationIndicator(response);
        if (lastModified && lastModified === currentLastModified) {
            this.#logger.debug('remote config unmodified');
            return {};
        }
        if (method === 'head') {
            response = await this.#fetch(url, { method: 'get', headers });
            lastModified = getModificationIndicator(response);
        }
        if (!response.ok) {
            throw new Error(`Failed to fetch remote config: HTTP ${response.status}`);
        }
        let body;
        try {
            body = await response.json();
        }
        catch {
            throw new Error('Failed to parse remote config: invalid JSON');
        }
        const { data } = body;
        if (!data || typeof data !== 'object') {
            throw new TypeError('invalid body data');
        }
        this.#logger.debug('remote config fetched');
        return { config: data, lastModified };
    };
}
const createRemoteConfig = ({ errorTracking, freeze, logger, fetch, config, getBuildMetadata, remoteConfigAtom, }) => {
    return new RemoteConfig({
        errorTracking,
        logger,
        fetch,
        freeze,
        config,
        getBuildMetadata,
        remoteConfigAtom,
    });
};
const remoteConfigDefinition = {
    id: 'remoteConfig',
    type: 'module',
    factory: createRemoteConfig,
    dependencies: [
        'fetch',
        'freeze',
        'logger',
        'remoteConfigAtom',
        'getBuildMetadata',
        'config?',
        'errorTracking',
    ],
    public: true,
};
export default remoteConfigDefinition;
